"use client";

import Link from "next/link";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { useTranslation } from "@/lib/i18n/context";
import { CompareSection } from "./CompareSection";
import { SocialProofSection } from "./SocialProofSection";
import { StoriesSection, type StoryCard } from "./StoriesSection";

export function HowItWorksSection({ stories = [] }: { stories?: StoryCard[] }) {
  const { t } = useTranslation();
  const { faq, finalCta } = t.landing.cover;

  // Soft scroll-pin on the closing CTA (mobile + desktop): the page pauses here
  // so the user lingers on the final invitation, and the trial button glows
  // brighter the further they scroll — the last, strongest nudge to begin.
  const ctaRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ctaRef, offset: ["start start", "end end"] });
  const buttonGlow = useTransform(scrollYProgress, [0.2, 0.6], [0, 1]);
  const buttonScale = useTransform(scrollYProgress, [0.2, 0.6], [1, 1.05]);

  return (
    <>
      <CompareSection />

      <SocialProofSection />

      <StoriesSection stories={stories} />

      <section id="faq" className="relative max-w-[760px] mx-auto px-5 md:px-12 py-20 md:py-[120px] z-[2]">
        <div className="text-left md:text-center mb-10">
          <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {faq.kicker}
          </div>
          <h2
            className="font-display text-[clamp(38px,4.2vw,58px)] leading-[1.04] tracking-[-0.02em] text-balance"
            style={{ fontWeight: 380 }}
          >
            {faq.headlineA} <em className="text-[var(--terracotta)] italic">{faq.headlineEm}</em>
          </h2>
        </div>
        {faq.items.map((item) => (
          <details key={item.q} className="group border-b border-[var(--hair)]">
            <summary
              className="cursor-pointer list-none flex justify-between items-center gap-4 py-5 min-h-[44px] font-display text-[20px] md:text-[22px] leading-[1.3] tracking-[-0.01em]"
              style={{ fontWeight: 400 }}
            >
              <span className="text-balance">{item.q}</span>
              <span className="w-4 h-4 flex-shrink-0 transition-transform group-open:rotate-180">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6l4 4 4-4" />
                </svg>
              </span>
            </summary>
            <p className="text-[16px] leading-[1.65] text-[var(--ink-soft)] pb-5 max-w-[60ch]">{item.a}</p>
          </details>
        ))}
      </section>

      {/* Closing CTA — soft scroll-pin (mobile + desktop); trial button glows on scroll. */}
      {/* Scroll-pause marker. The CTA is centred inside a sticky min-h-screen, so a
          default (header-clearance) stop lands the content too low. A negative
          `data-pause-offset` pins a touch BELOW the marker so the centred CTA sits
          near the middle of the viewport (kicker → button all comfortably framed). */}
      {/* Desktop-only hard-pin: on touch the CTA's own tall sticky pin already
          pauses the scroll, and a touchmove-preventing freeze here could trap the
          user before the footer. */}
      <div className="scroll-stop h-px" aria-hidden data-pause-offset="-80" data-pause-desktop-only="1" />
      <div ref={ctaRef} className="relative border-t border-[var(--hair)] mt-10 h-[165vh] lg:h-[300vh]">
        <div className="sticky top-0 flex min-h-[100svh] items-center lg:min-h-screen">
          <section className="relative text-center w-full max-w-[820px] mx-auto px-5 md:px-12 py-20 md:py-[110px] lg:py-0 z-[2]">
            <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {finalCta.kicker}
            </div>
            <h2
              className="font-display text-[clamp(40px,4.6vw,64px)] leading-[1.02] tracking-[-0.025em] text-balance"
              style={{ fontWeight: 380 }}
            >
              {finalCta.line1} <em className="text-[var(--terracotta)] italic">{finalCta.em}</em>
            </h2>
            <p className="mt-5 mb-8 text-[17px] md:text-[18px] leading-[1.6] text-[var(--ink-soft)] max-w-[60ch] mx-auto text-pretty">
              {finalCta.description}
            </p>
            <motion.div className="relative block md:inline-block" style={{ scale: buttonScale }}>
              {/* Glowing halo that ramps up with scroll + keeps pulsing. */}
              <motion.span
                aria-hidden
                className="jn-attn-glow pointer-events-none absolute -inset-2 rounded-[20px]"
                style={{ opacity: buttonGlow }}
              />
              <Link
                id="final-cta"
                href="/register"
                className="relative inline-flex w-full md:w-auto justify-center items-center gap-2 px-8 py-[18px] rounded-[14px] bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium text-[16px] transition hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]"
              >
                {finalCta.cta}
              </Link>
            </motion.div>
            <div className="font-mono text-xs text-[var(--ink-soft)] tracking-[0.08em] mt-4">{finalCta.micro}</div>
          </section>
        </div>
      </div>
    </>
  );
}
